/**
 * ReportingShared — the plumbing every Reporting section stands on.
 *
 * One filters object becomes one query string (buildAnalyticsQuery), every
 * section fetches its own /api/audit/analytics endpoint through useAnalytics,
 * and Section gives each of them the same collapsible card with a one-line
 * plain-language explainer and calm loading / empty / error states.
 */
import React, { useEffect, useState } from 'react';

export const API = process.env.REACT_APP_API_URL;

// The whitelisted filter keys the analytics router accepts.
export const FILTER_KEYS = ['year', 'quarter', 'subject_type', 'program', 'severity', 'classification', 'template_id', 'auditor_id'];

/** One filters object → one stable query string, empty values dropped. */
export function buildAnalyticsQuery(filters = {}) {
  const p = new URLSearchParams();
  for (const k of FILTER_KEYS) {
    const v = filters[k];
    if (v !== undefined && v !== null && String(v) !== '') p.set(k, String(v));
  }
  return p.toString();
}

/** Fetch one analytics endpoint; refetches whenever the query string changes. */
export function useAnalytics(path, qs) {
  const [state, setState] = useState({ loading: true, error: '', data: null });
  useEffect(() => {
    let alive = true;
    setState({ loading: true, error: '', data: null });
    fetch(`${API}/api/audit/analytics/${path}${qs ? `?${qs}` : ''}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        if (!alive) return;
        if (d && d.success) setState({ loading: false, error: '', data: d });
        else setState({ loading: false, error: (d && d.message) || 'This section could not load.', data: null });
      })
      .catch(() => { if (alive) setState({ loading: false, error: 'This section could not load. Check the connection and try again.', data: null }); });
    return () => { alive = false; };
  }, [path, qs]);
  return state;
}

// ── Number formatting ─────────────────────────────────────────────────────────

/** 0.873 → "87%" (rate is a 0..1 fraction). Null-safe. */
export const fmtPct = (rate, digits = 0) =>
  rate === null || rate === undefined ? 'n/a' : `${(Number(rate) * 100).toFixed(digits)}%`;

/** 12.4 → "12d". Null-safe. */
export const fmtDays = (d) => (d === null || d === undefined ? 'n/a' : `${Math.round(Number(d))}d`);

export const fmtNum = (n) => (n === null || n === undefined ? 'n/a' : Number(n).toLocaleString());

export const fmtDate = (v) => (v ? new Date(v).toLocaleDateString() : '');

// ── Collapsible section card ──────────────────────────────────────────────────

export function Section({ title, explainer, loading, error, empty, emptyText, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <button onClick={() => setOpen(o => !o)} aria-expanded={open} className="w-full flex items-center justify-between gap-3 text-left">
        <div>
          <div className="text-sm font-semibold text-slate-800">{title}</div>
          <div className="text-[11px] text-slate-400 mt-0.5">{explainer}</div>
        </div>
        <svg className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
      </button>
      {open && (
        <div className="mt-3">
          {loading ? (
            <div className="text-xs text-slate-400 py-4">Loading…</div>
          ) : error ? (
            <div className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>
          ) : empty ? (
            <div className="text-xs text-slate-400 bg-slate-50 border border-dashed border-slate-200 rounded-lg px-3 py-4 text-center">{emptyText || 'Nothing here for this period yet.'}</div>
          ) : children}
        </div>
      )}
    </div>
  );
}

/** text-[10px] uppercase micro-label used above sub-blocks inside sections. */
export function MicroLabel({ children }) {
  return <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">{children}</div>;
}
